import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 20;

const safeText = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "N/A" : String(v);

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

const SellerProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");

  // pagination
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = (localStorage.getItem("role") || "").toLowerCase().trim();

    if (!token || role !== "admin") {
      navigate("/login");
      return;
    }

    const fetchApprovedProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axios.get(`${baseurl}/api/product`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.products)
          ? res.data.products
          : [];

        // ✅ You were filtering approved === true (so it's not "unapproved")
        const approvedProducts = list.filter((p) => p?.approved === true);
        setProducts(approvedProducts);
      } catch (err) {
        console.error("Error fetching products:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };

    fetchApprovedProducts();
  }, [navigate]);

  // ✅ pagination derived
  const totalItems = products.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = useMemo(
    () => products.slice(startIndex, startIndex + PAGE_SIZE),
    [products, startIndex]
  );

  useEffect(() => {
    // keep page in range if list size changes
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleViewProduct = async (productId) => {
    try {
      setDetailsLoading(true);
      setError("");

      const res = await axios.get(`${baseurl}/api/admin/payment-details/${productId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setSelectedProduct({
        productDetails: res.data?.productDetails || {},
        otherPayment: res.data?.otherPayment || null,
        paymentDetails: res.data?.paymentDetails || null,
      });
    } catch (err) {
      console.error("Error fetching product details:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to fetch product details.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeSidebar = () => setSelectedProduct(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Seller Products (Approved)
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Click <b>View</b> to see full product + payment details.
            </p>
          </div>

          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Total: <b>{totalItems}</b> • Page: <b>{page}</b>/<b>{totalPages}</b>
          </div>
        </div>

        {error && (
          <div className="mb-5 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">#</th>
                <th className="px-4 py-3 text-left font-semibold">Product</th>
                <th className="px-4 py-3 text-left font-semibold">Max Qty</th>
                <th className="px-4 py-3 text-left font-semibold">Price</th>
                <th className="px-4 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    No products found.
                  </td>
                </tr>
              ) : (
                pageItems.map((product, idx) => (
                  <tr key={product._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">
                      {startIndex + idx + 1}
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">
                        {safeText(product?.name)}
                      </div>
                      <div className="text-xs text-gray-500 font-mono break-all">
                        {safeText(product?._id)}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      {safeText(product?.maxquantity)}
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      ₹{inr(product?.price)}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleViewProduct(product._id)}
                          className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing{" "}
                <b>{totalItems === 0 ? 0 : startIndex + 1}</b> to{" "}
                <b>{Math.min(startIndex + PAGE_SIZE, totalItems)}</b> of{" "}
                <b>{totalItems}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                {(() => {
                  const maxBtns = 7;
                  let start = Math.max(1, page - Math.floor(maxBtns / 2));
                  let end = Math.min(totalPages, start + maxBtns - 1);
                  start = Math.max(1, end - maxBtns + 1);

                  const pages = [];
                  for (let p = start; p <= end; p++) pages.push(p);

                  return pages.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-2 rounded-lg border ${
                        p === page
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                      }`}
                    >
                      {p}
                    </button>
                  ));
                })()}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      {selectedProduct && (
        <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl border-l border-gray-200 p-6 overflow-y-auto z-50">
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-black text-2xl"
            onClick={closeSidebar}
          >
            &times;
          </button>

          <div className="pr-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {safeText(selectedProduct.productDetails?.name)}
            </h2>
            <p className="text-xs text-gray-500 font-mono break-all">
              {safeText(selectedProduct.productDetails?._id)}
            </p>
          </div>

          {detailsLoading ? (
            <div className="mt-6 text-sm text-gray-600">Loading details…</div>
          ) : (
            <div className="mt-6 space-y-6">
              {/* Product Details */}
              <section className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-blue-700 mb-3">
                  Product Details
                </h3>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>
                    <span className="font-medium">Description:</span>{" "}
                    {safeText(selectedProduct.productDetails?.description)}
                  </p>
                  <p>
                    <span className="font-medium">Price:</span> ₹
                    {inr(selectedProduct.productDetails?.price)}
                  </p>
                  <p>
                    <span className="font-medium">Max Quantity:</span>{" "}
                    {safeText(selectedProduct.productDetails?.maxquantity)}
                  </p>
                  <p>
                    <span className="font-medium">Min Quantity:</span>{" "}
                    {safeText(selectedProduct.productDetails?.minquantity)}
                  </p>
                  <p>
                    <span className="font-medium">Stock:</span>{" "}
                    {safeText(selectedProduct.productDetails?.stock)}
                  </p>
                  <p>
                    <span className="font-medium">Category:</span>{" "}
                    {safeText(selectedProduct.productDetails?.category)}
                  </p>
                  <p>
                    <span className="font-medium">Subcategory:</span>{" "}
                    {safeText(selectedProduct.productDetails?.subcategory)}
                  </p>
                  <p>
                    <span className="font-medium">GST:</span>{" "}
                    {safeText(selectedProduct.productDetails?.gstNumber)}
                  </p>
                  <p>
                    <span className="font-medium">HSN:</span>{" "}
                    {safeText(selectedProduct.productDetails?.hsnCode)}
                  </p>
                </div>
              </section>

              {/* Seller */}
              <section className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-green-700 mb-3">
                  Seller Details
                </h3>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>
                    <span className="font-medium">Name:</span>{" "}
                    {safeText(selectedProduct.productDetails?.seller?.name)}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span>{" "}
                    {safeText(selectedProduct.productDetails?.seller?.email)}
                  </p>
                  <p>
                    <span className="font-medium">Mobile:</span>{" "}
                    {safeText(selectedProduct.productDetails?.seller?.mobile)}
                  </p>
                  <p>
                    <span className="font-medium">Address:</span>{" "}
                    {safeText(selectedProduct.productDetails?.seller?.address?.street)}
                    {", "}
                    {safeText(selectedProduct.productDetails?.seller?.address?.city)}
                    {", "}
                    {safeText(selectedProduct.productDetails?.seller?.address?.state)}
                    {", "}
                    {safeText(selectedProduct.productDetails?.seller?.address?.country)}
                    {" - "}
                    {safeText(selectedProduct.productDetails?.seller?.address?.postalCode)}
                  </p>
                </div>
              </section>

              {/* Other Payment */}
              {selectedProduct.otherPayment && (
                <section className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-yellow-700 mb-3">
                    Other Payment
                  </h3>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <span className="font-medium">Method:</span>{" "}
                      {safeText(selectedProduct.otherPayment?.method)}
                    </p>
                    <p>
                      <span className="font-medium">Amount:</span> ₹
                      {inr(selectedProduct.otherPayment?.amount)}
                    </p>
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      {selectedProduct.otherPayment?.date
                        ? new Date(selectedProduct.otherPayment.date).toLocaleDateString("en-IN")
                        : "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Bank:</span>{" "}
                      {safeText(selectedProduct.otherPayment?.bankName)}
                    </p>
                    <p>
                      <span className="font-medium">A/C:</span>{" "}
                      {safeText(selectedProduct.otherPayment?.accountNumber)}
                    </p>
                    <p>
                      <span className="font-medium">IFSC:</span>{" "}
                      {safeText(selectedProduct.otherPayment?.ifscCode)}
                    </p>
                    <p>
                      <span className="font-medium">Txn:</span>{" "}
                      {safeText(selectedProduct.otherPayment?.transactionId)}
                    </p>
                  </div>
                </section>
              )}

              {/* Payment Details */}
              <section className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-purple-700 mb-3">
                  Payment Details
                </h3>

                <div className="text-sm text-gray-700 space-y-3">
                  {(selectedProduct.paymentDetails?.products || []).length > 0 ? (
                    (selectedProduct.paymentDetails.products || []).map((p, i) => (
                      <div key={`${p?.productId || "prod"}_${i}`} className="border-b border-gray-200 pb-3">
                        <p>
                          <span className="font-medium">Product ID:</span>{" "}
                          {safeText(p?.productId)}
                        </p>
                        <p>
                          <span className="font-medium">Product Name:</span>{" "}
                          {safeText(p?.productName)}
                        </p>
                        <p>
                          <span className="font-medium">Visibility:</span>{" "}
                          {safeText(p?.visibilityLevel)}
                        </p>
                        <p>
                          <span className="font-medium">Visibility Amount:</span>{" "}
                          ₹{inr(p?.visibilityAmount)}
                        </p>
                        <p>
                          <span className="font-medium">Power Pack:</span>{" "}
                          {p?.powerPack ? "Yes" : "No"}
                        </p>
                        <p>
                          <span className="font-medium">Power Pack Amount:</span>{" "}
                          ₹{inr(p?.powerPackAmount)}
                        </p>
                        <p>
                          <span className="font-medium">Total Product Amount:</span>{" "}
                          ₹{inr(p?.totalProductAmount)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No payment-products data.</p>
                  )}

                  <p>
                    <span className="font-medium">Total Amount:</span>{" "}
                    ₹{inr(selectedProduct.paymentDetails?.totalAmount)}
                  </p>
                  <p>
                    <span className="font-medium">Status:</span>{" "}
                    {safeText(selectedProduct.paymentDetails?.paymentStatus)}
                  </p>
                  <p>
                    <span className="font-medium">Razorpay Order:</span>{" "}
                    {safeText(selectedProduct.paymentDetails?.razorpayOrderId)}
                  </p>
                  <p>
                    <span className="font-medium">Razorpay Payment:</span>{" "}
                    {safeText(selectedProduct.paymentDetails?.razorpayPaymentId)}
                  </p>
                </div>
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SellerProductsPage;








































// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { baseurl } from "../config/url";
// import { useNavigate } from "react-router-dom";


// const SellerProductsPage = () => {
//   const [products, setProducts] = useState([]);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const navigate = useNavigate();

//   useEffect(() => {

//     const token = localStorage.getItem("token");
//     // const role = localStorage.getItem("role");
//     const role = (localStorage.getItem("role") || "").toLowerCase().trim();

//     if (!token || role !== "admin") {
//       // Redirect to login page if not authorized
//       navigate("/login");
      
//       return;
//     }
//     console.log(typeof role, role === "admin");
//     console.log("TOKEN", localStorage.getItem("token"));
//     console.log("ROLE", localStorage.getItem("role"));


//     const fetchUnapprovedProducts = async () => {
//       try {
//         const response = await axios.get(`${baseurl}/api/product`, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });

//         if (response.data?.products) {
//           const unapprovedProducts = response.data.products.filter(
//             (product) => product.approved === true
//           );
//           setProducts(unapprovedProducts);
//         } else {
//           setProducts([]);
//         }
//       } catch (err) {
//         console.error("Error fetching products:", err);
//         setError(
//           err.response?.data?.message || "Failed to fetch unapproved products"
//         );
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchUnapprovedProducts();
//   }, [navigate]);

  
//   const handleViewProduct = async (productId) => {
//     try {
//       const response = await axios.get(
//         `${baseurl}/api/admin/payment-details/${productId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("token")}`,
//           },
//         }
//       );

//       setSelectedProduct({
//         productDetails: response.data?.productDetails || {},
//         otherPayment: response.data?.otherPayment || null,
//         paymentDetails: response.data?.paymentDetails || null,
//       });
//     } catch (err) {
//       console.error("Error fetching product details:", err);
//       alert(err.response?.data?.message || "Failed to fetch product details.");
//     }
//   };

//   const closeSidebar = () => {
//     setSelectedProduct(null);
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-r from-gray-50 to-blue-50 py-10 px-4 lg:px-16">
//       <div className="relative flex">
//         {/* Product List */}
//         <div
//           className={`flex-1 p-6 bg-white rounded-lg shadow-lg ${
//             selectedProduct ? "mr-96" : ""
//           }`}
//         >
//           <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-12">
//             Approve Products
//           </h1>
//           <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//             <table className="table-auto w-full text-sm border-collapse">
//               <thead>
//                 <tr className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
//                   <th className="px-6 py-4 text-left font-bold">Product Name</th>
//                   <th className="px-6 py-4 text-left font-bold">Max Quantity</th>
//                   <th className="px-6 py-4 text-left font-bold">Price</th>
//                   <th className="px-6 py-4 text-center font-bold">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {products.length > 0 ? (
//                   products.map((product, index) => (
//                     <tr
//                       key={product._id}
//                       className={`${
//                         index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
//                       } hover:bg-gray-200 transition-colors duration-300`}
//                     >
//                       <td className="px-6 py-4 text-gray-800 font-semibold">
//                         {product.name}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.maxquantity}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">₹{product.price}</td>
//                       <td className="px-6 py-4 text-center">
//                         <button
//                           onClick={() => handleViewProduct(product._id)}
//                           className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-1 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 transition-transform duration-300 transform hover:scale-105"
//                         >
//                           View
//                         </button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan="4"
//                       className="px-6 py-4 text-center text-gray-600 font-medium"
//                     >
//                       No unapproved products found.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Sidebar for Product Details */}
//         {selectedProduct && (
//   <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 rounded-l-lg p-6 overflow-y-auto transition-transform transform translate-x-0">
//     {/* Close Button */}
//     <button
//       className="absolute top-4 right-4 text-gray-400 hover:text-black text-2xl focus:outline-none"
//       onClick={closeSidebar}
//     >
//       &times;
//     </button>

//     {/* Header */}
//     <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">
//       {selectedProduct.productDetails?.name || "Product Name"}
//     </h2>

//     {/* Product Details */}
//     <div className="space-y-6">
//       <div>
//         <h3 className="text-lg font-semibold text-blue-600">Product Details</h3>
//         <div className="text-sm text-gray-700 mt-2">
//           <p><span className="font-medium">Description:</span> {selectedProduct.productDetails?.description || "N/A"}</p>
//           <p><span className="font-medium">Price:</span> ₹{selectedProduct.productDetails?.price || "N/A"}</p>
//           <p><span className="font-medium">Max Quantity:</span> {selectedProduct.productDetails?.maxquantity || "N/A"}</p>
//           <p><span className="font-medium">Min Quantity:</span> {selectedProduct.productDetails?.minquantity || "N/A"}</p>
//           <p><span className="font-medium">Stock:</span> {selectedProduct.productDetails?.stock || "N/A"}</p>
//           <p><span className="font-medium">Category:</span> {selectedProduct.productDetails?.category || "N/A"}</p>
//           <p><span className="font-medium">Subcategory:</span> {selectedProduct.productDetails?.subcategory || "N/A"}</p>
//           <p><span className="font-medium">GST Number:</span> {selectedProduct.productDetails?.gstNumber || "N/A"}</p>
//           <p><span className="font-medium">HSN Code:</span> {selectedProduct.productDetails?.hsnCode || "N/A"}</p>
//         </div>
//       </div>

//       {/* Seller Details */}
//       <div>
//         <h3 className="text-lg font-semibold text-green-600">Seller Details</h3>
//         <div className="text-sm text-gray-700 mt-2">
//           <p><span className="font-medium">Name:</span> {selectedProduct.productDetails?.seller?.name || "N/A"}</p>
//           <p><span className="font-medium">Email:</span> {selectedProduct.productDetails?.seller?.email || "N/A"}</p>
//           <p><span className="font-medium">Mobile:</span> {selectedProduct.productDetails?.seller?.mobile || "N/A"}</p>
//           <p><span className="font-medium">Address:</span> {selectedProduct.productDetails?.seller?.address?.street || "N/A"}, {selectedProduct.productDetails?.seller?.address?.city || "N/A"}, {selectedProduct.productDetails?.seller?.address?.state || "N/A"}, {selectedProduct.productDetails?.seller?.address?.country || "N/A"} - {selectedProduct.productDetails?.seller?.address?.postalCode || "N/A"}</p>
//         </div>
//       </div>

//       {/* Other Payment Details */}
//       {selectedProduct.otherPayment && (
//         <div>
//           <h3 className="text-lg font-semibold text-yellow-600">Other Payment</h3>
//           <div className="text-sm text-gray-700 mt-2">
//             <p><span className="font-medium">Method:</span> {selectedProduct.otherPayment.method}</p>
//             <p><span className="font-medium">Amount:</span> ₹{selectedProduct.otherPayment.amount}</p>
//             <p><span className="font-medium">Date:</span> {new Date(selectedProduct.otherPayment.date).toLocaleDateString()}</p>
//             <p><span className="font-medium">Bank Name:</span> {selectedProduct.otherPayment.bankName}</p>
//             <p><span className="font-medium">Account Number:</span> {selectedProduct.otherPayment.accountNumber}</p>
//             <p><span className="font-medium">IFSC Code:</span> {selectedProduct.otherPayment.ifscCode}</p>
//             <p><span className="font-medium">Transaction Id:</span>
//             {selectedProduct.otherPayment.transactionId}</p>

//           </div>
//         </div>
//       )}

//       {/* Payment Details */}
//       <div>
//         <h3 className="text-lg font-semibold text-purple-600">Payment Details</h3>
//         <div className="text-sm text-gray-700 mt-2">
//           {selectedProduct.paymentDetails?.products.map((product, index) => (
//             <div key={index} className="mb-4 border-b pb-2">
//               <p><span className="font-medium">Product ID:</span> {product.productId || "N/A"}</p>
//               <p><span className="font-medium">Product Name:</span> {product.productName || "N/A"}</p>
//               <p><span className="font-medium">Visibility Level:</span> {product.visibilityLevel || "N/A"}</p>
//               <p><span className="font-medium">Visibility Amount:</span> ₹{product.visibilityAmount || "N/A"}</p>
//               <p><span className="font-medium">Power Pack:</span> {product.powerPack ? "Yes" : "No"}</p>
//               <p><span className="font-medium">Power Pack Amount:</span> ₹{product.powerPackAmount || "N/A"}</p>
//               <p><span className="font-medium">Total Product Amount:</span> ₹{product.totalProductAmount || "N/A"}</p>
//             </div>
//           ))}
//           <p><span className="font-medium">Total Amount:</span> ₹{selectedProduct.paymentDetails?.totalAmount || "N/A"}</p>
//           <p><span className="font-medium">Payment Status:</span> {selectedProduct.paymentDetails?.paymentStatus || "N/A"}</p>
//           <p><span className="font-medium">Razorpay Order ID:</span> {selectedProduct.paymentDetails?.razorpayOrderId || "N/A"}</p>
//           <p><span className="font-medium">Razorpay Payment ID:</span> {selectedProduct.paymentDetails?.razorpayPaymentId || "N/A"}</p>
//         </div>
//       </div>
//     </div>
//   </div>
// )}

//       </div>
//     </div>
//   );
// };

// export default SellerProductsPage;
